/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { Icon } from '@fluentui/react/lib/Icon';
import { List } from '@fluentui/react/lib/List';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import { Text } from '@fluentui/react/lib/Text';
import { ITextFieldProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisSeparator } from 'src/core/components/LuisSeparator';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { AnalyticsDropdown } from 'src/luis/projects/analytics/components/TrainingEvaluation/AnalyticsDropdown';
import { useIntentsWithErrorsCardTracker } from 'src/luis/projects/analytics/hooks/useIntentsWithErrorsCardTracker';
import { FiltersTypes } from 'src/luis/projects/analytics/types/Filters';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';

export type FaultyIntentsListProps = {
    /**
     * List of intents.
     */
    intents: IntentMetadata[];

    /**
     * List of the most problematic intents.
     */
    mostProblematicIntents: IntentMetadata[];

    /**
     * Callback when the selected intent changes.
     */
    onSelectedIntentChange: (intent: IntentMetadata) => void;
};

const messages = defineMessages({
    filterLabel: {
        id: 'analytics.intentsWithErrorsCard.filterLabel',
        defaultMessage: 'Filters:'
    },
    mostProblematicIntentsOption: {
        id: 'analytics.intentsWithErrorsCard.mostProblematicIntentsOption',
        defaultMessage: 'most problematic intents'
    },
    incorrectOption: {
        id: 'analytics.intentsWithErrorsCard.incorrectOption',
        defaultMessage: 'incorrect predictions above'
    },
    unclearOption: {
        id: 'analytics.intentsWithErrorsCard.unclearOption',
        defaultMessage: 'unclear predictions above'
    },
    correctOption: {
        id: 'analytics.intentsWithErrorsCard.correctOption',
        defaultMessage: 'correct predictions below'
    },
    noIntentsMsg: {
        id: 'analytics.intentsWithErrorsCard.noIntentsMsg',
        defaultMessage: 'No items found.'
    }
});

const { getClassName, styleDiv } = getStylist('Dashboard');
const dashboardTheme = getScopedTheme('Dashboard');
const getClassNames = classNamesFunction<ITextFieldProps, ITextFieldStyles>();

const ListWrapper = styleDiv('ListWrapper', theme => ({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    justifyContent: 'center',
    height: theme.intentsListHeight,
    marginLeft: theme.detailedFaultyIntentPercentLegendSpacing
}));

const ratioInputClassName = getClassName('ratioInputClassName', theme => ({
    textAlign: 'center',
    border: theme.dropdownBorder,
    padding: theme.ratioTextFieldPadding,
    borderRadius: theme.dropdownBorderRadius
}));

const ratioFieldStyles = getClassNames({
    fieldGroup: {
        border: 'none',
        fontSize: dashboardTheme.xSmallFont,
        color: dashboardTheme.defaultTextColor,
        width: dashboardTheme.ratioTextFieldWidth,
        height: dashboardTheme.dropdownSmallHeight,
        lineHeight: dashboardTheme.mediumLineHeight,
        letterSpacing: dashboardTheme.xLargeLetterSpacing
    }
});

const emptyMsg = getClassName('emptyMsg', theme => ({
    color: theme.statsComparisonColor
}));

/**
 * Displays the faulty intents list with ability to be changed through different
 * filter options.
 *
 * @param props The properties to display the intents list.
 */
export const FaultyIntentsList = (props: FaultyIntentsListProps) => {
    const { intents, onSelectedIntentChange, mostProblematicIntents } = props;
    const { uiState, setFilterRatio, setAppliedFilter, setSelectedIntentIndex } = useIntentsWithErrorsCardTracker();
    const { localize } = useLocalization();

    const eventLoggerRef = React.useRef(getEventLogger('FaultyIntentsList'));

    const filterOptions: IDropdownOption[] = [
        { key: 'incorrect', text: localize(messages.incorrectOption) },
        { key: 'unclear', text: localize(messages.unclearOption) },
        { key: 'correct', text: localize(messages.correctOption) },
        { key: 'mostProblematic', text: localize(messages.mostProblematicIntentsOption) }
    ];

    let displayedIntents = mostProblematicIntents;

    React.useEffect(() => onSelectedIntentChange(displayedIntents[uiState.selectedIntentIndex]), [
        uiState.appliedFilter,
        uiState.filterRatio,
        intents
    ]);

    if (uiState.appliedFilter !== 'mostProblematic') {
        displayedIntents = sortAndFilterIntents(intents, uiState.appliedFilter, uiState.filterRatio);
    }

    return (
        <Stack>
            <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 10, padding: dashboardTheme.detailedFaultyIntentPercentLegendSpacing }}>
                <Text>{localize(messages.filterLabel)}</Text>
                <AnalyticsDropdown
                    isSearchEnabled={false}
                    options={filterOptions}
                    selectedKey={uiState.appliedFilter}
                    onRenderCaretDown={() => <Icon iconName="CaretDownSolid8" />}
                    onChange={(_, filter) => {
                        eventLoggerRef.current.log('FilterDropdownSelectionChange');

                        if (uiState.appliedFilter === 'mostProblematic' || filter.key === 'mostProblematic') {
                            setFilterRatio(getRatio(filter.key as FiltersTypes));
                        }
                        setSelectedIntentIndex(0);
                        setAppliedFilter(filter.key as FiltersTypes);
                    }}
                />
                <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
                    <LuisTextField
                        value={uiState.filterRatio}
                        onChange={(_, newRatio) => {
                            setFilterRatio(newRatio);
                        }}
                        placeholder=""
                        styles={ratioFieldStyles}
                        inputClassName={ratioInputClassName}
                        disabled={uiState.appliedFilter === 'mostProblematic'}
                    />
                    <Text>%</Text>
                </Stack>
            </Stack>

            <ListWrapper>
                {displayedIntents.length ? (
                    <ScrollablePane>
                        <FocusZone direction={FocusZoneDirection.vertical} isCircularNavigation>
                            <List
                                // Need to spread out the array to create new array which will force
                                // the List component to re-render.
                                items={[...displayedIntents]}
                                onRenderCell={(item, index) => (
                                    <Row
                                        item={item}
                                        selected={uiState.selectedIntentIndex === index}
                                        onClick={() => {
                                            eventLoggerRef.current.log('IntentListSelectionChange');
                                            setSelectedIntentIndex(index);
                                            onSelectedIntentChange(displayedIntents[index]);
                                        }}
                                        mode={uiState.appliedFilter}
                                    />
                                )}
                            />
                        </FocusZone>
                    </ScrollablePane>
                ) : (
                    <Text className={emptyMsg}>{localize(messages.noIntentsMsg)}</Text>
                )}
            </ListWrapper>

            <LuisSeparator />
        </Stack>
    );
};

const rowClass = getClassName('rowClass', theme => ({
    cursor: 'pointer',
    padding: theme.intentsListItemSpacing
}));

/**
 * Displays the intent row
 *
 * @param props The properties of the given intent.
 */
const Row = (props: { item: IntentMetadata; selected: boolean; onClick: () => void; mode: FiltersTypes }) => {
    const { item, onClick, selected, mode } = props;

    let percentagePart;
    switch (mode) {
        case 'correct':
            percentagePart = <Text>{item.correctAccuracy.toFixed(1)}%</Text>;
            break;
        case 'incorrect':
            percentagePart = <Text>{item.incorrectAccuracy.toFixed(1)}%</Text>;
            break;
        case 'unclear':
            percentagePart = <Text>{item.unclearAccuracy.toFixed(1)}%</Text>;
            break;
        default:
    }

    return (
        <Stack
            data-is-focusable
            className={mergeStyles(rowClass, { background: selected ? dashboardTheme.selectedIntentBackgroundColor : 'transparent' })}
            horizontal
            onClick={onClick}>
            <Text>{item.intentName}</Text>
            <Stack grow={1} horizontalAlign="end">
                {percentagePart}
            </Stack>
        </Stack>
    );
};

/**
 * Gets the suitable ratio to the given selected filter.
 *
 * @param selectedFilter The current selected filter.
 * @returns The ratio.
 */
const getRatio = (selectedFilter: FiltersTypes): string => {
    switch (selectedFilter) {
        case 'correct':
            return '60';
        case 'incorrect':
        case 'unclear':
            return '15';
        case 'mostProblematic':
            return '—';
        default:
    }
};

/**
 * Sorts the given intents according to the given currently selected filter and current ratio.
 *
 * @param intents Array to be sorted.
 * @param selectedFilter The current selected filter.
 * @param ratio The current used ratio.
 * @returns Sorted array with the given params.
 */
const sortAndFilterIntents = (intents: IntentMetadata[], selectedFilter: FiltersTypes, ratio: string): IntentMetadata[] => {
    switch (selectedFilter) {
        case 'correct':
            return intents.filter(i => i.correctAccuracy < Number(ratio)).sort((a, b) => (a.correctAccuracy < b.correctAccuracy ? -1 : 1));
        case 'incorrect':
            return intents
                .filter(i => i.incorrectAccuracy > Number(ratio))
                .sort((a, b) => (a.incorrectAccuracy > b.incorrectAccuracy ? -1 : 1));
        case 'unclear':
            return intents.filter(i => i.unclearAccuracy > Number(ratio)).sort((a, b) => (a.unclearAccuracy > b.unclearAccuracy ? -1 : 1));
        default:
    }
};
